import React, { useState, createContext, useContext, useEffect } from "react";
import Response from "../utils/response";
import {
  Client,
  contractAddress,
  Provider,
  VerifiedWallet,
} from "@verified-network/verified-sdk";
import { useVerifiedSecuritiesList } from "../context/verifiedWalletData";
import config from "../services/config/homestead.json";
import axios from "axios";
import { Config, useAccount, useClient, useConnectorClient } from "wagmi";
import {
  getProviderNetwork,
  getProvider,
  getWeb3,
} from "../utils/helpers/networks";
import { countryDetails, networks } from "../utils/constants";
import CLIENTABI from "../utils/abi/Client.json";
import Web3 from "web3";
import { from } from "@apollo/client";
import { getKycChallenge } from "../pages/kycManager/kycUtils";

const { ethers, providers } = require("ethers");

const GlobalContext = createContext(null);

export const GlobalProvider = ({ children }) => {
  const { address, chainId } = useAccount();
  const { data: client } = useConnectorClient({ chainId });
  const { chain, transport } = client || { chain: null, transport: null };
  let network, provider;
  if (chain) {
    network = getProviderNetwork(chain);
  }

  if (network) {
    provider = getProvider(transport, network);
  }
  let signer, web3;
  if (provider && provider.getSigner && chainId && address) {
    signer = provider.getSigner(address);
    web3 = getWeb3(transport);
  }

  // Using the useState hook to keep track of the value authed (if a user is logged in)
  const [userRole, setUserRole] = useState("investor");
  const [userSignature, setUserSignature] = useState("");
  const [userSignatureNonce, setUserSignatureNonce] = useState("");
  const [isSecondaryManager, setSecondaryManager] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [issuedSecondary, setIssuedSecondary] = useState();
  const [kycStatus, setKYCstatus] = useState(0);
  const [userType, setUserType] = useState("");
  const [userCountry, setUserCountry] = useState("");
  const [userCountryCode, setUserCountryCode] = useState("");
  const [userETHBalance, setUserETHBalance] = useState("");
  const [connectedProvider, setConnectedProvider] = useState(null);
  const [connectedWeb3, setConnectedWeb3] = useState(null);
  const [connectedSigner, setConnectedSigner] = useState(null);
  const [connectedChainId, setConnectedChainId] = useState(null);
  const [userAddress, setUserAddress] = useState(null);
  const [verifiedWalletDetails, setVerifiedWalletDetails] = useState({});
  const [userMarginTransaction, setUserMarginTransaction] = useState([]);

  const login = async (verifiedWalletDetails) => {
    setGlobalLoading(true);
    const result = await fetchUserRole(verifiedWalletDetails);

    if (result) {
      setUserRole(result);
      if (result?.toLowerCase() === "kycaml") {
        const { message, nonce } = await getKycChallenge(
          verifiedWalletDetails &&
            Object.values(verifiedWalletDetails)?.length > 0 &&
            verifiedWalletDetails?.pk?.startsWith("0x")
            ? verifiedWalletDetails?.address
            : address?.length > 0
              ? address
              : "",
        );
        if (message && nonce) {
          try {
            let signature;
            if (
              verifiedWalletDetails &&
              Object.values(verifiedWalletDetails)?.length > 0 &&
              verifiedWalletDetails?.pk?.startsWith("0x")
            ) {
              const sender = new VerifiedWallet(verifiedWalletDetails?.pk);
              const _chainId = Object.keys(networks).includes(
                String(verifiedWalletDetails?.chainId),
              )
                ? Number(verifiedWalletDetails?.chainId)
                : Number(Object.keys(networks)[0]);
              if (sender && networks[_chainId]?.rpcUrls) {
                const signerVerified = sender.setProvider(
                  new Provider(networks[_chainId]?.rpcUrls[0], _chainId),
                );
                signature = await signerVerified.signMessage(message);
              }
            } else if (signer) {
              signature = await signer.signMessage(message);
            }

            setUserSignature(signature);
            setUserSignatureNonce(nonce);
          } catch (err) {
            setUserSignature("");
            setUserSignatureNonce("");
          }
        }
      }
    }
    setGlobalLoading(false);
  };

  const removeDetails = () => {
    setUserAddress(null);
    setConnectedChainId(null);
    setConnectedProvider(null);
    setConnectedSigner(null);
    setConnectedWeb3(null);
    setKYCstatus(0);
    setUserType("");
    setUserCountry("");
    setUserCountryCode("");
    setLoggedIn(false);
    setUserMarginTransaction([]);
  };

  const fetchUserRole = async (verifiedWalletDetails) => {
    let providerVerified, web3Verified, signerVerified, clientAddress;

    if (
      verifiedWalletDetails &&
      Object.values(verifiedWalletDetails)?.length > 0 &&
      verifiedWalletDetails?.address
    ) {
      setVerifiedWalletDetails(verifiedWalletDetails);
      const _chainId = Object.keys(networks).includes(
        String(verifiedWalletDetails?.chainId),
      )
        ? Number(verifiedWalletDetails?.chainId)
        : Number(Object.keys(networks)[0]);

      clientAddress = contractAddress[_chainId]?.Client;

      //step 2: get required network and user details from wagmi;
      let sender;
      if (
        verifiedWalletDetails?.pk &&
        verifiedWalletDetails?.pk?.startsWith("0x") &&
        _chainId > 0
      ) {
        sender = new VerifiedWallet(verifiedWalletDetails?.pk);
      } else if (
        //disconnect on invalid/incomplete verifiedWalletDetails to allow other connections
        (verifiedWalletDetails?.address?.length > 0 &&
          !verifiedWalletDetails?.pk) ||
        (verifiedWalletDetails?.address?.length > 0 &&
          !verifiedWalletDetails?.pk?.startsWith("0x"))
      ) {
        localStorage.removeItem("myVault");
      }

      //initialize provider, signer, privatekey and vault contract
      if (sender && networks[_chainId]?.rpcUrls) {
        providerVerified = new Provider(
          networks[_chainId]?.rpcUrls[0],
          _chainId,
        );
      }

      if (providerVerified) {
        signerVerified = sender.setProvider(providerVerified);
        web3Verified = new Web3(networks[_chainId]?.rpcUrls[0]);
      }
    } else {
      setVerifiedWalletDetails({});
      clientAddress = contractAddress[chainId]?.Client;
    }

    if (
      (clientAddress && provider?.getSigner && signer) ||
      (providerVerified && web3Verified && clientAddress && signerVerified)
    ) {
      let clientContract, clietContractWeb3;

      if (
        verifiedWalletDetails &&
        Object.values(verifiedWalletDetails)?.length > 0 &&
        providerVerified &&
        web3Verified &&
        verifiedWalletDetails?.address &&
        clientAddress &&
        signerVerified
      ) {
        const _chainId = Object.keys(networks).includes(
          String(verifiedWalletDetails?.chainId),
        )
          ? Number(verifiedWalletDetails?.chainId)
          : Number(Object.keys(networks)[0]);
        setUserAddress(verifiedWalletDetails?.address);
        setConnectedProvider(providerVerified);
        setConnectedSigner(signerVerified);
        setConnectedWeb3(web3Verified);
        setConnectedChainId(_chainId);
        clietContractWeb3 = new web3Verified.eth.Contract(
          CLIENTABI,
          clientAddress,
        );
      } else if (
        !verifiedWalletDetails ||
        Object.values(verifiedWalletDetails)?.length === 0
      ) {
        if (clientAddress && provider?.getSigner && web3 && address && signer) {
          setUserAddress(address);
          setConnectedProvider(provider);
          setConnectedSigner(signer);
          setConnectedWeb3(web3);
          setConnectedChainId(chainId);
          clientContract = new Client(signer, clientAddress);
        }
      }

      if (clientContract || clietContractWeb3) {
        const data = clietContractWeb3
          ? await clietContractWeb3.methods
              .getRole(verifiedWalletDetails?.address)
              .call({ from: verifiedWalletDetails?.address })
              .then((response) => Response.parseBytes32Value(response[0]))
              .then((role) => {
                return role;
              })
          : address?.length > 0 && Number(chainId) > 0
            ? await clientContract
                .getRole(address)
                .then((response) => Response.array(response))
                .then((response) => Response.parseBytes32Value(response[0]))
                .then((role) => {
                  return role;
                })
            : "";
        const kycDataResponse = clietContractWeb3
          ? await clietContractWeb3.methods
              .getClientKYC(verifiedWalletDetails?.address)
              .call({ from: verifiedWalletDetails?.address })
          : address?.length > 0 && Number(chainId) > 0
            ? await clientContract.getClientKYC(address)
            : null;
        const fullKYCResponse = clietContractWeb3
          ? await clietContractWeb3.methods
              .getFullClientKYC(verifiedWalletDetails?.address)
              .call({ from: verifiedWalletDetails?.address })
          : address?.length > 0 && Number(chainId) > 0
            ? await clientContract.getFullClientKYC(address)
            : null;
        const result = clietContractWeb3
          ? kycDataResponse
          : kycDataResponse?.response?.result;
        const kycStatus = Number(result[3]);
        const fullKYCResult = clietContractWeb3
          ? fullKYCResponse
          : fullKYCResponse?.response?.result;
        const userCountry = Response.parseBytes32Value(result[2]);
        const userCountryDetails = countryDetails.find(
          (cont) => cont.name.toLowerCase() === userCountry.toLowerCase(),
        );
        setUserCountry(userCountry);
        if (userCountryDetails) {
          setUserCountryCode(userCountryDetails.code);
        }
        const balanceRaw = web3Verified
          ? await web3Verified.eth.getBalance(verifiedWalletDetails?.address)
          : await getWeb3(transport).eth.getBalance(address);
        setUserETHBalance(ethers.utils.formatEther(balanceRaw));
        setKYCstatus(kycStatus);
        setUserType(fullKYCResult[1]);

        setLoggedIn(true);
        return data;
      }
    }
  };

  return (
    // Using the provider. components can use the values.
    <GlobalContext.Provider
      value={{
        userRole,
        userSignature,
        userSignatureNonce,
        setUserSignature,
        setUserSignatureNonce,
        setUserRole,
        login,
        removeDetails,
        isUpdate,
        setIsUpdate,
        globalLoading,
        setGlobalLoading,
        loggedIn,
        kycStatus,
        userType,
        isSecondaryManager,
        setSecondaryManager,
        issuedSecondary,
        setIssuedSecondary,
        userCountry,
        userCountryCode,
        userETHBalance,
        userAddress,
        setUserAddress,
        connectedProvider,
        setConnectedProvider,
        connectedSigner,
        setConnectedSigner,
        connectedWeb3,
        setConnectedWeb3,
        connectedChainId,
        setConnectedChainId,
        verifiedWalletDetails,
        setVerifiedWalletDetails,
        userMarginTransaction,
        setUserMarginTransaction,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

// Finally creating the custom hook
export const useGlobal = () => useContext(GlobalContext);

export const fetchSumsubAccessToken = async (account, networkId) => {
  return axios({
    method: "GET",
    url: `${config.sumsubEndpoint}?account=${account}&networkId=${networkId}`,
  })
    .then((res) => {
      return {
        accessToken: res.data.token,
      };
    })
    .catch((err) => {
      console.log("err: ", err?.message);
    });
};
